import { useState, useEffect, useRef } from 'react'
import { useGameStore } from '@store/gameStore'
import { GAME_CONFIG } from '@config/gameConfig'
import { GameLayout } from '@components/GameLayout'
import { SecondChanceModal } from '@components/SecondChanceModal'
import './RankingScreen.css'
import './RankingScreen_vs.css'
import './RankingScreen_layout.css'

/**
 * Calculate player rank based on their power.
 * Higher power = lower (better) rank.
 * Formula: We use the inverse of power scaling to determine rank.
 */
const calculateRankFromPower = (playerPower: number): number => {
    const topPlayerPower = GAME_CONFIG.endgame.topPlayerPower
    const scaling = GAME_CONFIG.endgame.powerScaling

    // If player power >= top player, they're rank 1
    if (playerPower >= topPlayerPower) return 1

    // Calculate rank using logarithm of power ratio
    // rank = 1 + log(topPower / playerPower) / log(scaling)
    const rank = 1 + Math.floor(Math.log(topPlayerPower / playerPower) / Math.log(scaling))

    return Math.min(Math.max(rank, 1), GAME_CONFIG.endgame.startingRank)
}

/**
 * Generate opponent at a given rank.
 * The opponent is ALWAYS slightly stronger than someone at that rank.
 */
const generateOpponent = (opponentRank: number) => {
    const names = [
        'P2W_Whale_King', 'xX_Kratos_Xx', 'ShadowLord99', 'DiamondHands420',
        'MegaGamer2024', 'UltraWhale', 'CashMoney_Pro', 'TopPlayer_EU',
        'PayMaster3000', 'GemCollector', 'VIPMember_Gold', 'SpeedRunner_X',
    ]

    const basePower = GAME_CONFIG.endgame.topPlayerPower
    const powerReduction = Math.pow(GAME_CONFIG.endgame.powerScaling, opponentRank - 1)
    const npcPower = Math.floor(basePower / powerReduction)

    return {
        rank: opponentRank,
        name: names[(opponentRank - 1) % names.length],
        power: npcPower,
    }
}

// Generate mini ranking for sidebar
const generateTopPlayers = () => {
    const names = [
        'P2W_Whale_King', 'xX_Kratos_Xx', 'ShadowLord99', 'DiamondHands420', 'MegaGamer2024'
    ]
    const players = []

    for (let rank = 1; rank <= 5; rank++) {
        const basePower = GAME_CONFIG.endgame.topPlayerPower
        const powerReduction = Math.pow(GAME_CONFIG.endgame.powerScaling, rank - 1)
        players.push({
            rank,
            name: names[rank - 1],
            power: Math.floor(basePower / powerReduction),
        })
    }

    return players
}

export function RankingScreen() {
    const [opponent, setOpponent] = useState<ReturnType<typeof generateOpponent> | null>(null)
    const [topPlayers, setTopPlayers] = useState<ReturnType<typeof generateTopPlayers>>([])
    const [isBattling, setIsBattling] = useState(false)
    const [battleResult, setBattleResult] = useState<'win' | 'lose' | null>(null)
    const [showSecondChance, setShowSecondChance] = useState(false)
    const hasSeenLossOffer = useRef(false)

    const power = useGameStore(state => state.power)
    const equipment = useGameStore(state => state.equipment)
    const stats = useGameStore(state => state.stats)
    const setScreen = useGameStore(state => state.setScreen)
    const setRank = useGameStore(state => state.setRank)
    const setPhase = useGameStore(state => state.setPhase)
    const addPower = useGameStore(state => state.addPower)
    const spendGems = useGameStore(state => state.spendGems)
    const resources = useGameStore(state => state.resources)
    const heroSkin = useGameStore(state => state.heroSkin)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)

    // Calculate total power including equipment bonuses
    const equipmentPower = Object.values(equipment).reduce((sum, item) => {
        return sum + (item?.power || 0)
    }, 0)

    // Stats also contribute to power (each stat point = 5 power)
    const statPower = (stats.atk + stats.def + stats.spd) * 5

    const totalPower = power + equipmentPower + statPower

    // Hero Image Map
    const HERO_IMAGES: { [key: string]: string } = {
        'default': '/rookie.png',
        'knight': '/ritter.png',
        'king': '/king.png',
        'wizard': '/magier.png'
    }

    // Calculate current rank from total power (dynamic!)
    const calculatedRank = calculateRankFromPower(totalPower)

    // Update rank in store and generate opponent
    useEffect(() => {
        setRank(calculatedRank)

        if (tutorialPhase === 'pvp') {
            setOpponent({
                rank: calculatedRank - 1,
                name: 'Anfänger Bob',
                power: Math.max(10, totalPower - 500), // Guaranteed win
            })
        } else if (calculatedRank > 1) {
            setOpponent(generateOpponent(calculatedRank - 1))
        } else {
            setOpponent(null) // At rank 1, no opponent needed
        }

        // Generate top players list, injecting the user
        const players = generateTopPlayers()

        // If user is not in top 5, replace the last one or add a separator logic visually
        // For now, let's just make sure if we are in top 5, we are there.
        // If not, we could show top 3 + user's rank.

        // Simplified: Just use the static list but if user is in that range, overwrite? 
        // Better: Create a display list.

        // Let's stick to the simple static top 5 for now, but render the USER'S rank row separately in the UI if not in top 5.
        // Actually, the user asked "mit dem namen wird man dann auch im Ranking eingespeichert".
        // It implies seeing oneself. 

        setTopPlayers(players)
    }, [power, calculatedRank, setRank])

    // Calculate power difference using total power (NOT boosted)
    const powerDiff = opponent ? ((opponent.power - totalPower) / totalPower * 100) : 0
    const isStronger = totalPower > (opponent?.power || 0)
    const winChance = opponent ? Math.min(99, Math.max(1, Math.round(totalPower / opponent.power * 50))) : 0
    // Boost gives 15% more power
    const boostMultiplier = 1.15
    const boostedPower = Math.floor(totalPower * boostMultiplier)
    const boostIncrease = boostedPower - totalPower

    const executeBattle = (boosted: boolean) => {
        if (!opponent) return

        if (boosted && !spendGems(100)) return

        setIsBattling(true)
        setBattleResult(null)

        setTimeout(() => {
            let playerPower = totalPower
            if (boosted) playerPower = boostedPower

            const won = playerPower > opponent.power

            if (won) {
                setRank(opponent.rank)
                addPower(100)
                setBattleResult('win')

                if (tutorialPhase === 'pvp') {
                    setTutorialPhase('finale')
                } else if (opponent.rank === 1) {
                    setTimeout(() => {
                        setPhase('finished')
                        setScreen('end')
                    }, 2000)
                }
            } else {
                setBattleResult('lose')
                // Show Second Chance popup on first loss
                if (!hasSeenLossOffer.current) {
                    hasSeenLossOffer.current = true
                    setTimeout(() => setShowSecondChance(true), 1000)
                }
            }

            setIsBattling(false)
        }, 1500)
    }

    const resetBattle = () => {
        setBattleResult(null)
        setOpponent(generateOpponent(calculatedRank))
    }

    // Already #1
    if (calculatedRank === 1) {
        return (
            <GameLayout>
                <div className="pvp">
                    <div className="pvp__victory">
                        <span className="pvp__victory-crown">👑</span>
                        <h1>DU BIST #1!</h1>
                        <p>Du hast die Spitze erreicht!</p>
                        <button className="btn btn--gold btn--lg" onClick={() => setScreen('end')}>
                            Ergebnis ansehen
                        </button>
                    </div>
                </div>
            </GameLayout>
        )
    }

    return (
        <GameLayout>
            {/* Second Chance Modal for Battle Loss */}
            <SecondChanceModal
                trigger="battle_lost"
                isOpen={showSecondChance}
                onClose={() => setShowSecondChance(false)}
            />

            <div className="pvp">
                {/* Hero Title */}
                <div className="pvp__hero">
                    <div className="pvp__hero-title">
                        <h1><span style={{ background: 'none', WebkitTextFillColor: 'initial' }}>⚔️</span> PVP ARENA</h1>
                        <p>Kämpfe dich an die Spitze!</p>
                    </div>
                </div>

                {/* Main Battle Area */}
                {opponent && !battleResult && !isBattling && (
                    <>
                        {/* VS Arena (Characters) */}
                        <div className="pvp__vs-section">
                            <div className="pvp__vs-arena">
                                {/* Player Character */}
                                <div className="pvp__vs-character pvp__vs-character--player">
                                    <div className="pvp__vs-name-badge">{useGameStore.getState().username}</div>
                                    <div className="pvp__vs-char-image">
                                        <img src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default} alt="Du" />
                                    </div>
                                    <div className="pvp__vs-power-badge">
                                        <span className="pvp__vs-power-icon">⚔️</span>
                                        <span className="pvp__vs-power-value">{totalPower.toLocaleString()}</span>
                                    </div>
                                </div>

                                {/* VS Divider */}
                                <div className="pvp__vs-divider">
                                    <div className="pvp__vs-text">VS</div>
                                </div>

                                {/* Opponent Character */}
                                <div className="pvp__vs-character pvp__vs-character--opponent">
                                    <div className="pvp__vs-name-badge">{opponent.name}</div>
                                    <div className="pvp__vs-char-image pvp__vs-char-image--flipped">
                                        <img src={HERO_IMAGES[['knight', 'king', 'wizard'][opponent.rank % 3]] || HERO_IMAGES.default} alt="Gegner" />
                                    </div>
                                    <div className="pvp__vs-power-badge pvp__vs-power-badge--enemy">
                                        <span className="pvp__vs-power-icon">⚔️</span>
                                        <span className="pvp__vs-power-value">{opponent.power.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Battle Actions */}
                        <div className="pvp__actions">
                            <button
                                id="pvp-attack-btn"
                                className="btn btn--gold btn--lg btn--full btn--shine"
                                onClick={() => executeBattle(false)}
                            >
                                ⚔️ Angreifen
                            </button>


                            {/* Battle Info Box (Diff + Chance) */}
                            <div className="pvp__info-box">
                                <div className={`pvp__power-diff ${isStronger ? 'pvp__power-diff--ahead' : 'pvp__power-diff--behind'}`}>
                                    {isStronger ? (
                                        <span>✅ Du bist {Math.abs(powerDiff).toFixed(0)}% stärker</span>
                                    ) : (
                                        <span>⚠️ Gegner ist {Math.abs(powerDiff).toFixed(0)}% stärker</span>
                                    )}
                                </div>
                                <div className="pvp__win-chance">
                                    <span>Chance: </span>
                                    <span className={`pvp__win-value ${winChance >= 50 ? 'pvp__win-value--good' : 'pvp__win-value--bad'}`}>
                                        {winChance}%
                                    </span>
                                </div>
                            </div>

                            <button
                                className="btn btn--gem btn--lg btn--full btn--split"
                                onClick={() => executeBattle(true)}
                                disabled={resources.gems < 100}
                            >
                                <span className="btn__main">Angriff mit +{boostIncrease.toLocaleString()} ⚔️</span>
                                <span className="btn__divider"></span>
                                <span className="btn__cost">100 💎</span>
                            </button>
                        </div>

                    </>
                )}

                {/* Battle Result Overlay/Area */}
                {battleResult && (
                    <div className={`pvp__result pvp__result--${battleResult}`}>
                        {battleResult === 'win' ? (
                            <>
                                <span className="pvp__result-icon">🎉</span>
                                <span className="pvp__result-text">SIEG! Du bist jetzt Rang #{calculatedRank}</span>
                            </>
                        ) : (
                            <>
                                <span className="pvp__result-icon">💀</span>
                                <span className="pvp__result-text">Niederlage - Werde stärker!</span>
                            </>
                        )}
                        <button className="btn btn--gold btn--lg" onClick={() => {
                            setBattleResult(null)
                            setIsBattling(false)
                            resetBattle() // Generate a new opponent for the current rank
                        }}>
                            Nächster Gegner
                        </button>
                    </div>
                )}

                {isBattling && (
                    <div className="pvp__battling">
                        <span className="pvp__battling-icon">⚔️</span>
                        <span>Kämpfe...</span>
                    </div>
                )}

                {/* Rank Progress (Bubbles) - Always visible or below battle interface */}
                <div className="pvp__rank-progress-section">
                    <div className="pvp__hero-ranks">
                        {/* Current Rank */}
                        <div className="pvp__hero-rank pvp__hero-rank--current">
                            <div className="pvp__rank-badge">
                                <div className="pvp__rank-badge-icon">#{calculatedRank}</div>
                            </div>
                            <span className="pvp__rank-label">Dein Rang</span>
                        </div>

                        {/* Progress Arrow */}
                        <div className="pvp__hero-arrow">
                            <div className="pvp__progress-line">
                                <div className="pvp__progress-fill"></div>
                            </div>
                            <span className="pvp__progress-arrow">→</span>
                        </div>

                        {/* Target Rank */}
                        <div className="pvp__hero-rank pvp__hero-rank--target">
                            <div className="pvp__rank-badge pvp__rank-badge--gold">
                                <div className="pvp__rank-badge-icon">#{calculatedRank - 1}</div>
                            </div>
                            <span className="pvp__rank-label">Ziel</span>
                        </div>
                    </div>
                </div>

            </div>

            {/* Mini Ranking */}
            <div className="pvp__ranking-mini">
                <div className="pvp__ranking-header">
                    <span>🏆 Ranking</span>
                </div>
                <div className="ranking__list">
                    <div className="ranking__header">
                        <span>Rang</span>
                        <span>Spieler</span>
                        <span>Macht</span>
                    </div>

                    {topPlayers.map(player => (
                        <div key={player.rank} className={`ranking__row ${player.rank === calculatedRank ? 'ranking__row--active' : ''}`}>
                            <span className={`ranking__rank rank-${player.rank}`}>#{player.rank}</span>
                            <div className="ranking__name-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {player.rank === calculatedRank ? (
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', overflow: 'hidden' }}>
                                        <img src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default} alt="Skin" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                ) : (
                                    <span>👤</span>
                                )}
                                <span className="ranking__name">
                                    {player.rank === calculatedRank ? useGameStore.getState().username : player.name}
                                </span>
                            </div>
                            <span className="ranking__power">⚔️ {player.power.toLocaleString()}</span>
                        </div>
                    ))}

                    {/* Show next 3 opponents ABOVE user if not in top 5 */}
                    {calculatedRank > 5 && (
                        <>
                            <div className="ranking__divider">...</div>

                            {/* Next 3 opponents */}
                            {[...Array(3)].map((_, i) => {
                                const nextRank = calculatedRank - (i + 1)
                                if (nextRank < 1 || nextRank <= 5) return null
                                const nextOpponent = generateOpponent(nextRank)
                                return (
                                    <div key={nextRank} className="ranking__row">
                                        <span className="ranking__rank">#{nextOpponent.rank}</span>
                                        <div className="ranking__name-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>👤</span>
                                            <span className="ranking__name">{nextOpponent.name}</span>
                                        </div>
                                        <span className="ranking__power">⚔️ {nextOpponent.power.toLocaleString()}</span>
                                    </div>
                                )
                            }).reverse()}

                            {/* Current user */}
                            <div className="ranking__row ranking__row--active">
                                <span className="ranking__rank">#{calculatedRank}</span>
                                <div className="ranking__name-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', overflow: 'hidden' }}>
                                        <img src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default} alt="Skin" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                    <span className="ranking__name">{useGameStore.getState().username}</span>
                                </div>
                                <span className="ranking__power">⚔️ {totalPower.toLocaleString()}</span>
                            </div>

                            {/* 1 player below user */}
                            {(() => {
                                const belowRank = calculatedRank + 1
                                const belowOpponent = generateOpponent(belowRank)
                                return (
                                    <div className="ranking__row">
                                        <span className="ranking__rank">#{belowOpponent.rank}</span>
                                        <div className="ranking__name-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>👤</span>
                                            <span className="ranking__name">{belowOpponent.name}</span>
                                        </div>
                                        <span className="ranking__power">⚔️ {belowOpponent.power.toLocaleString()}</span>
                                    </div>
                                )
                            })()}
                        </>
                    )}
                </div>
            </div>
        </GameLayout >
    )
}
